import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface FeatureFlags {
  [key: string]: boolean;
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagsV2Service {
  private flags$ = new BehaviorSubject<FeatureFlags>({});
  private readonly apiUrl = `${environment.url_servicios}/v2/features`;
  private loaded = false;
  private loadingRequest$: Observable<FeatureFlags> | null = null;

  private http = inject(HttpClient);

  /**
   * Load feature flags from the server.
   * Handles errors gracefully (e.g., when user is not authenticated).
   * Prevents duplicate simultaneous requests.
   */
  loadFlags(): Observable<FeatureFlags> {

    // If already loading, return the existing request
    if (this.loadingRequest$) {
      return this.loadingRequest$;
    }

    // Create new request and cache it
    this.loadingRequest$ = this.http.get<FeatureFlags>(this.apiUrl).pipe(
      tap((flags) => {
        this.flags$.next(flags);
        this.loaded = true;
        this.loadingRequest$ = null; // Clear loading state
      }),
      catchError((error) => {
        // If loading fails (e.g., not authenticated), just return empty flags
        console.warn('Failed to load feature flags:', error.message);
        this.flags$.next({});
        this.loaded = true;
        this.loadingRequest$ = null; // Clear loading state
        return of({});
      }),
      shareReplay(1) // Share the result with all subscribers
    );

    return this.loadingRequest$;
  }

  /**
   * Check if flags have been loaded at least once
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Observable check for a feature flag
   */
  isEnabled(flagName: string): Observable<boolean> {
    return this.flags$.pipe(map((flags) => !!flags[flagName]));
  }

  /**
   * Synchronous check for guards/resolvers
   */
  isEnabledSync(flagName: string): boolean {
    return !!this.flags$.value[flagName];
  }

  /**
   * Get all current flags
   */
  getAllFlags(): FeatureFlags {
    return this.flags$.value;
  }

  /**
   * Observable of all feature flags
   */
  getAllFlags$(): Observable<FeatureFlags> {
    return this.flags$.asObservable();
  }
}
